import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { CloseInputs, CloseTarget } from './copilot-domain';
import { isCloseComplete, validateCloseAgainstBook } from './copilot-validation';
import { toCalendarDateString } from './copilot-mapper';
import { TabState } from './copilot-ui-types';
import { CoPilotBookHedge } from './copilot-facade';
import { DetectMethodChanges } from '../../utils';
import { DateTime } from 'luxon';

interface DateItem {
    value: string;   // "yyyy-MM-dd" key
    label: string;
    date: Date;
}

// Close questionnaire (FR-18..22, FR-147, FR-148): multi-select of EXISTING book dates (no range)
// + target Hedge(s) | ALL (existing only). NO PriceRange/MinProfit/MaxRisk. Selections are
// constrained to the book the parent supplies; a pre-send guard double-checks existence.
@Component({
    selector: 'ets-hg-copilot-close-tab',
    templateUrl: 'hg-copilot-close-tab.component.html',
    styleUrls: ['./hg-copilot-close-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotCloseTabComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    @Output() stateChange = new EventEmitter<TabState<CloseInputs>>();

    // Target kind is a SEGMENTED BUTTON SET (dx-button-group), not a drop-down (owner ruling 2026-06-22).
    // Button captions are SHORT (the full option meaning would not fit a button); the full text shows
    // below as a hint line via targetHint.
    readonly targetKindButtons: { id: 'all' | 'hedges'; text: string }[] = [
        { id: 'all', text: 'All' },
        { id: 'hedges', text: 'Specific' }
    ];

    private readonly targetHints: { [key in 'all' | 'hedges']: string } = {
        all: 'ALL Existing Hedges (Within the Chosen Dates)',
        hedges: 'Specific Hedge(s)'
    };

    get targetHint(): string {
        return this.targetHints[this.targetKind];
    }

    dateItems: DateItem[] = [];
    hedgeItems: CoPilotBookHedge[] = [];

    selectedDateValues: string[] = [];
    targetKind: 'all' | 'hedges' = 'all';
    selectedHedgeIds: string[] = [];

    private _dateByValue: { [value: string]: Date } = {};

    // Existing book dates that may be closed (constrains the multi-select).
    @Input() set bookDates(dates: Date[]) {
        const items: DateItem[] = [];
        this._dateByValue = {};
        (dates || []).forEach(d => {
            const value = toCalendarDateString(d);
            this._dateByValue[value] = d;
            // Show weekday + friendly expiration ("Wed 23-Jun-26") per the screenshot — not the raw ISO
            // "yyyy-MM-dd". The ISO string stays the underlying value/key. d is local-midnight, so the
            // weekday/date are correct with no UTC shift.
            items.push({ value, label: DateTime.fromJSDate(d).toFormat('EEE dd-MMM-yy'), date: d });
        });
        items.sort((a, b) => a.date.getTime() - b.date.getTime());   // chronological (earliest expiration first)
        this.dateItems = items;
    }

    // Existing hedges that may be closed (constrains the target multi-select).
    @Input() set bookHedges(hedges: CoPilotBookHedge[]) {
        this.hedgeItems = hedges || [];
    }

    // CR-20260831-004: the fields that no longer match what the CURRENT run sent, diffed by the
    // orchestrator (hg-copilot-param-diff grammar: 'dates' / 'target.kind' / 'target.hedges').
    // Null/empty = clean (or no run yet).
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    isChanged(key: string): boolean {
        return !!(this.changedKeys && this.changedKeys[key]);
    }

    // dx-button-group click handler for the Target kind — set the discriminant, then re-run the
    // same change path the old select-box (onValueChanged)="onChange()" used.
    onTargetKind(e: { itemData: { id: 'all' | 'hedges' } }): void {
        // Dates are mandatory: the target cannot be chosen until at least one expiration is selected.
        // The dx-button-group is also [disabled] when there are no dates; this guard is the hard backstop.
        if (!this.hasSelectedDates) {
            return;
        }
        this.targetKind = e.itemData.id;
        this.onChange();
    }

    // Available Expirations: each book date is a toggle chip (multi-select). selectedDateValues holds the
    // ISO keys; the chip shows the friendly "EEE dd-MMM-yy" label.
    isDateSelected(value: string): boolean {
        return this.selectedDateValues.indexOf(value) >= 0;
    }

    // Dates are mandatory and gate the Target selector (the user cannot pick All/Specific until at least
    // one expiration is chosen).
    get hasSelectedDates(): boolean {
        return this.selectedDateValues.length > 0;
    }

    @DetectMethodChanges()
    toggleDate(value: string): void {
        const idx = this.selectedDateValues.indexOf(value);
        if (idx >= 0) {
            this.selectedDateValues = this.selectedDateValues.filter(v => v !== value);
        } else {
            this.selectedDateValues = this.selectedDateValues.concat([value]);
        }
        this.onChange();
    }

    // Target = Specific: the book hedges split into CALLS / PUTS, each a checkbox list so the user can tick
    // MULTIPLE hedges in one turn (owner ruling 2026-06-23 — replaces the old dropdown).
    get callHedges(): CoPilotBookHedge[] {
        return this.hedgesForSide('Call');
    }

    get putHedges(): CoPilotBookHedge[] {
        return this.hedgesForSide('Put');
    }

    // Hedges of one side, constrained to the SELECTED expirations — the date filter applies to the
    // Specific hedge list, not just the Run: a hedge shows only if it has a leg expiring on a chosen date.
    // With no date selected, no date filter (show all of that side). Keys are ISO "yyyy-MM-dd" on both
    // sides, so this is an exact set intersection.
    private hedgesForSide(side: 'Call' | 'Put'): CoPilotBookHedge[] {
        // Dates are mandatory: with NO expiration selected, show NO hedges (owner ruling 2026-06-23).
        if (this.selectedDateValues.length === 0) {
            return [];
        }
        const sideHedges = this.hedgeItems.filter(h => h.optionType === side);
        return sideHedges.filter(h => (h.expirations || []).some(e => this.selectedDateValues.indexOf(e) >= 0));
    }

    isHedgeSelected(id: string): boolean {
        return this.selectedHedgeIds.indexOf(id) >= 0;
    }

    @DetectMethodChanges()
    onHedgeToggle(id: string, selected: boolean): void {
        // CR-20260901-002: a hedge under a CoPilot Preview is display-only — the checkbox is [disabled],
        // this guard is the hard backstop (a disabled dx-check-box must not sneak a value change through).
        const hedge = this.hedgeItems.find(h => h.id === id);
        if (hedge && hedge.previewed) {
            return;
        }
        const idx = this.selectedHedgeIds.indexOf(id);
        if (selected && idx < 0) {
            this.selectedHedgeIds = this.selectedHedgeIds.concat([id]);
        } else if (!selected && idx >= 0) {
            this.selectedHedgeIds = this.selectedHedgeIds.filter(x => x !== id);
        }
        this.onChange();
    }

    onChange(): void {
        const inputs = this.buildInputs();
        const bookDateList = this.dateItems.map(i => i.date);
        // CR-20260901-002: previewed hedges are display-only — NOT valid run targets. A selected hedge
        // that becomes previewed drops out of the valid-target book and invalidates the form, exactly as
        // it did when previewing removed it from the list entirely.
        const bookHedgeIds = this.hedgeItems.filter(h => !h.previewed).map(h => h.id);
        const valid = isCloseComplete(inputs) && validateCloseAgainstBook(inputs, bookDateList, bookHedgeIds);
        this.stateChange.emit({ inputs, valid });
    }

    private buildInputs(): CloseInputs {
        const dates: Date[] = [];
        this.selectedDateValues.forEach(v => {
            const d = this._dateByValue[v];
            if (d) {
                dates.push(d);
            }
        });
        const target: CloseTarget = this.targetKind === 'all'
            ? { kind: 'all' }
            : { kind: 'hedges', hedgeIds: this.selectedHedgeIds.slice() };
        return { dates, target };
    }
}
